import {useCallback, useEffect, useMemo, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useTranslation} from 'react-i18next';
import {Box, Grid, Typography} from '@mui/material';

import ResponsiveFab from '@/components/ui/responsive-fab';
import DialogForm from '@/components/ui/dialogs/form-dialog';
import TeamCreateForm from '@/components/forms/groups/create';

import {useSnackbar} from '@/contexts/snackbar';

import LoadingScreen from '@/pages/loading-screen';

import {useAppSelector} from '@/redux-store/store';

import {findAllGroups} from '@/rest/groups/find-all-groups';
import {fetchNextPage} from '@/rest/utility/fetch-next-page';

import {GroupData} from '@/types/group';

import GroupCard from './group-card';
import GroupDetailsDialog from './details';
import StateSwitch from '@/components/ui/switches/state-switch';
import RefreshButton from '@/components/ui/buttons/refresh-button';
import {findMyCompanyGroups} from '@/rest/groups/find-my-company-groups';
import {useDispatch} from 'react-redux';
import {setGroupsInRedux} from '@/redux-store/slices/groups';
import {useCompanyContext} from '@/contexts/admin/companies';
import {MatrixRoomProvider} from '@/contexts/matrix/room/provider';
import MatrixUnreadEventsProvider from '@/contexts/matrix/unread-events/provider';
import {groupChangeObservable} from '@/rest/groups/change-observable';
import {env} from '@/config';
import GroupCardBig from './group-card-big';
import FilterForm from '@/components/filter';
import FilterButton from '@/components/filter/button';
import TextSearch from '../filter/text-search';
import {Filter} from '@/types/filter';
import FilterChip from '../filter/chip';
import {getActiveFilters, isNotEqualFilter} from '@/services/filter';
import {
  initialState,
  resetFilter,
  updateFilter
} from '@/redux-store/slices/filter';
import {getGroupById} from '@/rest/groups/get-group';
import {findTrainerGroups} from '@/rest/groups/find-trainer-groups';
import {useNavigate} from 'react-router-dom';
import {resetCurrentEvent, resetCurrentGroup} from '@/redux-store/slices/auth';
import {isOneOfUserCompaniesDocument, isPublicCompanyDocument} from '@/utils';
import RemoveFilterButton from '../ui/buttons/remove-filter-button';

interface GroupViewProps {
  groupId?: string;
  eventId?: string;
}
const GroupView: React.FC<GroupViewProps> = ({groupId, eventId}) => {
  const {t} = useTranslation();

  const {APP_VARIANT} = env;

  const {showSnackbarMessage} = useSnackbar();

  const [page, setPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState(true);
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<GroupData | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showCreateGroupForm, setShowCreateGroupForm] = useState(false);
  const [showCompanyGroups, setShowCompanyGroups] = useState(false);
  const [prevShowCompanyGroups, setPrevShowCompanyGroups] = useState(true);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const filter = useAppSelector((state) => state.filter);
  const groupFilter = filter['group'];
  const initialFilterState = initialState['group'];
  const activeFilters = getActiveFilters(
    groupFilter,
    initialFilterState,
    'group'
  );

  const {isAuthenticated, currentCompanyId} = useAppSelector(
    (state) => state.auth
  );
  const userInfos = useAppSelector((state) => state.userInfos.userInfos);
  const {myGroups} = useAppSelector((state) => state.groups);
  const {isUserHealthManager, isPublicCompany, appTag} = useAppSelector(
    (state) => state.auth
  );

  const {refreshCompanyData} = useCompanyContext() || {};
  const userRole = useAppSelector((state) => state.auth.keycloakUser?.userRole);
  const isUserTrainer = userRole === 'TRAINER';

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const companyId =
    APP_VARIANT === 'sjone' &&
    ((userInfos &&
      userInfos.companies &&
      userInfos.companies.length === 1 &&
      userInfos.companies[0].id._id) ||
      currentCompanyId);

  const userId = userInfos && userInfos._id;

  const fetchNextPageCallback = useCallback(async () => {
    if (!showCompanyGroups) {
      const selectedFilter = filter['group'];
      const fetchResult = await fetchNextPage(
        page,
        findAllGroups,
        20,
        selectedFilter,
        companyId || undefined,
        userId || undefined,
        appTag || undefined,
        undefined,
        isPublicCompany && !isAuthenticated
      );
      if (fetchResult !== undefined) {
        const {data, nextPage} = fetchResult;
        setPage(nextPage);
        setGroups([...groups, ...data]);
        setHasMore(data.length === 20);
      } else {
        //console.log('fetchresult undefined!?!');
      }
    }
  }, [
    showCompanyGroups,
    filter,
    page,
    companyId,
    groups,
    userId,
    appTag,
    isPublicCompany,
    isAuthenticated
  ]);

  const reloadCompanyGroups = useCallback(
    async (filterData?: Filter) => {
      const selectedFilter = filter['group'];
      const fetchedGroups = await findMyCompanyGroups(
        filterData || selectedFilter
      );
      dispatch(setGroupsInRedux(fetchedGroups));
    },
    [dispatch, filter]
  );

  const reloadGroups = useCallback(
    (filterData?: Filter) => {
      if (!showCompanyGroups) {
        const selectedFilter = filter['group'];
        fetchNextPage(
          0,
          findAllGroups,
          20,
          filterData || selectedFilter,
          companyId || undefined,
          userId || undefined,
          appTag || undefined,
          undefined,
          isPublicCompany && !isAuthenticated
        )
          .then((fetchResult) => {
            if (groups.length % 20 === 0 && page > 1) {
              setPage(0);
              setHasMore(true);
              setGroups([]);
              fetchNextPageCallback();
            } else if (fetchResult !== undefined) {
              const {data, nextPage} = fetchResult;
              setPage(nextPage);
              setGroups([...data]);
              setHasMore(data.length === 20);
            }
          })

          .catch(console.warn);
      }
    },
    [
      showCompanyGroups,
      filter,
      groups.length,
      page,
      fetchNextPageCallback,
      companyId,
      userId,
      appTag,
      isPublicCompany,
      isAuthenticated
    ]
  );

  const reloadTrainerGroups = useCallback(
    async (filterData?: Filter) => {
      const selectedFilter = filter['group'];
      const fetchedGroups = await findTrainerGroups(
        filterData || selectedFilter
      );

      dispatch(setGroupsInRedux(fetchedGroups));
    },
    [dispatch, filter]
  );

  const reloadGroupsBasedOnUserRole = useCallback(
    (filterData?: Filter) => {
      if (isUserTrainer && APP_VARIANT === 'sjone') {
        reloadTrainerGroups(filterData);
        setHasMore(false);
      } else {
        reloadGroups(filterData);
      }
    },
    [isUserTrainer, APP_VARIANT, reloadTrainerGroups, reloadGroups]
  );

  const handleShowCompanyGroupsChange = useCallback(
    (
      event: React.SyntheticEvent<HTMLElement | HTMLInputElement>,
      newValue: boolean
    ) => {
      setShowCompanyGroups(newValue);
      if (newValue) {
        setGroups([...myGroups]);
      } else {
        setGroups([]);
        setPage(0);
        setHasMore(true);
      }
    },
    [myGroups]
  );

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setSelectedGroup(null);
    if (groupId) {
      dispatch(resetCurrentGroup());
      if (appTag) {
        navigate(`/${appTag}`);
      } else {
        navigate('/');
      }
    }
  }, [appTag, dispatch, groupId, navigate]);

  const handleOpenGroupDetails = useCallback(() => {
    setDialogOpen(false);
    setSelectedGroup(null);
  }, []);

  const handleCreateNewGroup = useCallback(() => {
    setShowCreateGroupForm(true);
  }, []);

  const onGroupCreated = useCallback(async () => {
    showSnackbarMessage(t('group_create_success'), 'success');

    setShowCreateGroupForm(false);
    refreshCompanyData();
    reloadGroupsBasedOnUserRole(groupFilter);
  }, [
    reloadGroupsBasedOnUserRole,
    showSnackbarMessage,
    groupFilter,
    t,
    refreshCompanyData
  ]);

  const onGroupCreationError = useCallback(() => {
    showSnackbarMessage(t('group_create_failure'), 'error');
  }, [showSnackbarMessage, t]);

  const handleGroupCreationCancel = useCallback(() => {
    setShowCreateGroupForm(false);
  }, []);

  const onGroupCardClick = useCallback((group: GroupData) => {
    setSelectedGroup(group);
    setDialogOpen(true);
  }, []);

  const handleGroupChange = useCallback(
    (newData: GroupData) => {
      setGroups(
        groups.map((group) => (group._id === newData._id ? newData : group))
      );
    },
    [groups]
  );

  const handleGroupOfEventChanged = useCallback(() => {
    reloadGroupsBasedOnUserRole(groupFilter);
    if (APP_VARIANT === 'sjone') {
      reloadCompanyGroups(groupFilter);
    }
  }, [
    APP_VARIANT,
    reloadCompanyGroups,
    groupFilter,
    reloadGroupsBasedOnUserRole
  ]);

  const handleToggleFilterVisibility = useCallback(() => {
    setFilterVisible((prev) => !prev);
  }, []);

  const handleApplyFilter = useCallback(
    (hideFilter?: boolean, filterData?: Filter) => {
      if (hideFilter) {
        setFilterVisible(false);
      }
      if (showCompanyGroups) {
        reloadCompanyGroups(filterData);
      } else {
        reloadGroupsBasedOnUserRole(filterData);
      }
    },
    [reloadCompanyGroups, reloadGroupsBasedOnUserRole, showCompanyGroups]
  );

  const updateFilterData = useCallback(
    (data: Partial<Filter>) => {
      dispatch(updateFilter({type: 'group', data}));
    },
    [dispatch]
  );

  const handleRemoveFilter = useCallback(
    (filterKey: keyof Filter) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newFilterData: any = {...filter['group']};

      const value = initialFilterState[filterKey];

      newFilterData[filterKey] = value as Filter[keyof Filter];

      updateFilterData(newFilterData);
      handleApplyFilter(true, newFilterData);
    },
    [filter, initialFilterState, updateFilterData, handleApplyFilter]
  );

  const onEventClose = useCallback(() => {
    if (eventId) {
      dispatch(resetCurrentEvent());
      if (appTag) {
        navigate(`/${appTag}`);
      } else {
        navigate('/');
      }
    }
  }, [appTag, dispatch, eventId, navigate]);

  const handleResetFilter = useCallback(() => {
    dispatch(resetFilter({type: 'group'}));
    setFilterResetKey((prevKey) => prevKey + 1);
    handleApplyFilter(false, initialState['group']);
  }, [dispatch, handleApplyFilter]);

  const globalFilterChanged = useMemo(() => {
    return isNotEqualFilter(groupFilter, initialFilterState);
  }, [groupFilter, initialFilterState]);

  useEffect(() => {
    if (showCompanyGroups !== prevShowCompanyGroups) {
      setPrevShowCompanyGroups(showCompanyGroups);
      if (showCompanyGroups) {
        setHasMore(false);
      } else {
        reloadGroupsBasedOnUserRole(groupFilter);
      }
    }

    const subscription = groupChangeObservable.subscribe({
      next: (changeGroup) => {
        if (changeGroup.type === 'change') {
          const found = groups.findIndex((e) => e._id === changeGroup.data._id);
          if (found >= 0) {
            const newGroups = [...groups];
            newGroups.splice(found, 1, changeGroup.data);
            setGroups(newGroups);
          }
        } else if (changeGroup.type === 'new') {
          setGroups((prevGroups) => [changeGroup.data, ...prevGroups]);
        } else if (changeGroup.type === 'deleted') {
          setGroups((prevGroups) =>
            prevGroups.filter((group) => group._id !== changeGroup.id)
          );
        }
      }
    });

    if (showCompanyGroups || (isUserTrainer && APP_VARIANT === 'sjone')) {
      setGroups(myGroups);
    }

    return () => {
      subscription.unsubscribe();
    };
  }, [
    showCompanyGroups,
    prevShowCompanyGroups,
    myGroups,
    reloadGroups,
    groups,
    filter,
    groupFilter,
    reloadGroupsBasedOnUserRole,
    isUserTrainer,
    APP_VARIANT
  ]);

  useEffect(() => {
    const openGroupDialog = async () => {
      if (groupId) {
        const group = await getGroupById(groupId);
        if (APP_VARIANT === 'sjone') {
          if (
            !isOneOfUserCompaniesDocument(group, userInfos) &&
            !isPublicCompanyDocument(group, currentCompanyId, isPublicCompany)
          ) {
            navigate('/403');
          }
        }
        if (group) {
          setSelectedGroup(group);
          setDialogOpen(true);
        }
      }
    };
    openGroupDialog();
  }, [
    APP_VARIANT,
    groupId,
    navigate,
    userInfos,
    currentCompanyId,
    isPublicCompany
  ]);

  return (
    <>
      <Grid container marginTop={3}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: {xs: 'center', sm: 'flex-end'},
            width: '100%',
            marginBottom: {xs: 1, sm: 0}
          }}
        >
          {isUserHealthManager && (
            <StateSwitch
              value={showCompanyGroups}
              onChange={handleShowCompanyGroupsChange}
              labelOn={'show_company_groups'}
              labelOff={'show_all_groups'}
            />
          )}

          <RefreshButton
            label={'reload_groups'}
            onClick={() =>
              showCompanyGroups
                ? reloadCompanyGroups(groupFilter)
                : reloadGroupsBasedOnUserRole(groupFilter)
            }
          />
          {(globalFilterChanged || filterVisible) && (
            <RemoveFilterButton
              label={'reset_filter'}
              onClick={handleResetFilter}
            />
          )}
        </Box>
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: {xs: 'column', sm: 'row'},
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: {xs: 0, sm: 2}
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                marginTop: {
                  xs: 0,
                  sm: activeFilters.length > 0 && !filterVisible ? 0 : 1.5
                },
                marginBottom: {
                  xs: 0,
                  sm: activeFilters.length > 0 && !filterVisible ? 4.5 : 0
                },
                order: {xs: 1, sm: 2}
              }}
            >
              <FilterButton
                onClick={handleToggleFilterVisibility}
                documentType={'groups'}
                filterVisible={filterVisible}
              />
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                order: {xs: 2, sm: 1},
                width: {xs: '100%', sm: 'auto'}
              }}
            >
              <TextSearch
                searchType="group"
                searchLabel="group_search"
                onApply={handleApplyFilter}
              />
              {!filterVisible && activeFilters.length > 0 && (
                <Box marginTop={2} display="flex" flexWrap="wrap" gap={1}>
                  {activeFilters.map((filter) => (
                    <FilterChip
                      key={filter.value}
                      filter={filter}
                      onRemove={handleRemoveFilter}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
          {filterVisible && (
            <Box mt={1}>
              <FilterForm
                key={filterResetKey}
                filterType="group"
                onApply={(newFilterData, hideFilter) =>
                  handleApplyFilter(hideFilter, newFilterData)
                }
                hideFilterOnApply={true}
              />
            </Box>
          )}
        </Grid>
      </Grid>

      <InfiniteScroll
        dataLength={groups.length}
        next={fetchNextPageCallback}
        hasMore={hasMore}
        loader={<LoadingScreen />}
        endMessage={
          <Typography
            variant="body1"
            align="center"
            marginTop={2}
            paddingBottom={2}
          >
            <b>{t('saw_all_groups')}</b>
          </Typography>
        }
      >
        <Grid
          container
          rowSpacing={5}
          columnSpacing={4}
          sx={{
            marginTop: 3
          }}
          maxWidth={'lg'}
        >
          {' '}
          {groups.map((group, index) => {
            return (
              <MatrixUnreadEventsProvider documentId={group._id} key={index}>
                {APP_VARIANT === 'sjone' ? (
                  <GroupCard
                    group={group}
                    onChange={(newData) => {
                      if (group._id === newData._id) {
                        handleGroupChange(newData);
                      }
                    }}
                    onDelete={(groupId) => {
                      if (group._id === groupId) {
                        //setGroups(groups.filter((group) => group._id !== groupId));
                        reloadGroupsBasedOnUserRole(groupFilter);
                      }
                    }}
                    onGroupCardClick={onGroupCardClick}
                  />
                ) : (
                  <GroupCardBig
                    key={index}
                    group={group}
                    onGroupCardClick={onGroupCardClick}
                    cardMediaHeight="150"
                    alwaysTruncate={true}
                  />
                )}
              </MatrixUnreadEventsProvider>
            );
          })}
          {selectedGroup && (
            <MatrixUnreadEventsProvider documentId={selectedGroup._id}>
              <MatrixRoomProvider childId={selectedGroup._id}>
                <GroupDetailsDialog
                  onClose={handleDialogClose}
                  group={selectedGroup}
                  eventId={eventId}
                  open={dialogOpen}
                  onChange={(newData) => {
                    if (selectedGroup._id === newData._id) {
                      setSelectedGroup(newData);
                      handleGroupChange(newData);
                    }
                  }}
                  onDelete={(groupId) => {
                    if (selectedGroup._id === groupId) {
                      setDialogOpen(false);
                      //setGroups(groups.filter((group) => group._id !== groupId));
                      setSelectedGroup(null);
                      reloadGroupsBasedOnUserRole(groupFilter);
                    }
                  }}
                  groupOfEventChanged={handleGroupOfEventChanged}
                  onEventClose={onEventClose}
                  onCloseGroupDetails={handleOpenGroupDetails}
                />
              </MatrixRoomProvider>
            </MatrixUnreadEventsProvider>
          )}
          {isAuthenticated && (
            <Grid container justifyContent="flex-end" padding={2}>
              <ResponsiveFab
                onClick={handleCreateNewGroup}
                title={t('new_group_button')}
              />
            </Grid>
          )}
        </Grid>
      </InfiniteScroll>

      <DialogForm
        open={showCreateGroupForm}
        onCancel={handleGroupCreationCancel}
        onSubmit={onGroupCreated}
        onError={onGroupCreationError}
        FormComponent={TeamCreateForm}
        maxHeightAllowed="1200px"
      />
    </>
  );
};

export default GroupView;
